import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { Download, ChevronDown, ChevronRight } from "lucide-react";
import { getMyRole } from "@/lib/admin.functions";
import {
  getMyAnalyses,
  getMyApplications,
  type FounderAnalysis,
  type FounderApplication,
} from "@/lib/founder.functions";
import { getOnePagerContent } from "@/lib/onepager.functions";
import { claimDeck } from "@/lib/deck.functions";
import { buildOnePagerPdf } from "@/lib/onepager-pdf";
import { SidebarLayout } from "@/components/sidebar-layout";
import { IkomatchCTA } from "@/components/ikomatch-cta";
import { AutofillProfileTab } from "@/components/autofill-profile-tab";
import { usePricing } from "@/lib/pricing";

type DashboardTab = "overview" | "autofill";
type DashboardSearch = { tab?: DashboardTab };

export const Route = createFileRoute("/dashboard")({
  validateSearch: (s: Record<string, unknown>): DashboardSearch => ({
    tab: s.tab === "autofill" ? "autofill" : "overview",
  }),
  head: () => ({
    meta: [
      { title: "Dashboard - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DashboardPage,
});

type FounderData = {
  analyses: FounderAnalysis[];
  freeUsed: number;
  freeLimit: number;
  applications: FounderApplication[];
};

function DashboardPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/dashboard" });
  const activeTab: DashboardTab = search.tab === "autofill" ? "autofill" : "overview";
  const runGetMyRole = useServerFn(getMyRole);
  const runGetAnalyses = useServerFn(getMyAnalyses);
  const runGetApplications = useServerFn(getMyApplications);
  const runClaimDeck = useServerFn(claimDeck);
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "ready"; role: string; userId: string; founder?: FounderData }
  >({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { waitForSession } = await import("@/lib/auth-ready");
      const session = await waitForSession();
      if (!session) {
        if (!cancelled) navigate({ to: "/login", search: { next: "/dashboard" } });
        return;
      }

      try {
        const { role } = await runGetMyRole();
        if (cancelled) return;
        if (role === "super_admin" || role === "admin") {
          navigate({ to: "/admin" });
          return;
        }
        if (role === "investor") {
          navigate({ to: "/investor" });
          return;
        }

        // Attach the deck the user just unlocked (precise - not a browser-wide sweep)
        try {
          const pending = typeof window !== "undefined" ? localStorage.getItem("deckscore_pending_deck") : null;
          if (pending) {
            await runClaimDeck({ data: { deckId: pending } });
            localStorage.removeItem("deckscore_pending_deck");
          }
        } catch { /* non-fatal */ }
        const [a, ap] = await Promise.all([runGetAnalyses(), runGetApplications()]);
        if (cancelled) return;
        setState({
          status: "ready",
          role,
          userId: session.user.id,
          founder: {
            analyses: a.analyses,
            freeUsed: a.freeUsed,
            freeLimit: a.freeLimit,
            applications: ap.applications,
          },
        });
      } catch {
        if (!cancelled) setState({ status: "ready", role: "founder", userId: session.user.id });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, runGetMyRole, runGetAnalyses, runGetApplications, runClaimDeck]);

  const dashboardItems = [
    { to: "/dashboard", label: "Dashboard" },
    { to: "/upload", label: "Analyze a deck" },
  ];

  const tabs: { key: DashboardTab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "autofill", label: "Autofill Profile" },
  ];

  return (
    <SidebarLayout
      area="Dashboard"
      items={dashboardItems}
      promo={
        state.status === "ready" && state.founder ? (
          <FounderPromo
            freeUsed={state.founder.freeUsed}
            freeLimit={state.founder.freeLimit}
          />
        ) : undefined
      }
    >
      {state.status === "loading" ? (
        <p className="text-ink-secondary">Loading…</p>
      ) : (
        <>
          <div className="mb-6 flex items-center gap-1 border-b border-line">
            {tabs.map((t) => {
              const active = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => navigate({ to: "/dashboard", search: { tab: t.key } })}
                  className={`px-4 py-2.5 text-[13.5px] font-medium border-b-2 -mb-px transition ${
                    active
                      ? "border-accent text-ink"
                      : "border-transparent text-ink-secondary hover:text-ink"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
          {activeTab === "autofill" ? (
            <AutofillProfileTab userId={state.userId} />
          ) : state.founder ? (
            <FounderDashboard data={state.founder} />
          ) : (
            <div className="bg-surface border border-line rounded-2xl p-10">
              <p className="font-script text-accent text-[22px]">welcome back</p>
              <h1 className="font-display text-[28px] font-semibold mt-2">Your dashboard</h1>
              <p className="text-ink-secondary mt-3">We couldn't load your data. Try refreshing.</p>
            </div>
          )}
        </>
      )}
    </SidebarLayout>
  );
}

function FounderPromo({ freeUsed, freeLimit }: { freeUsed: number; freeLimit: number }) {
  const pricing = usePricing();
  const capped = freeUsed >= freeLimit;
  return (
    <div className="flex flex-col gap-2">
      <span
        className={`inline-flex w-full items-center justify-center rounded-full border px-3 py-1 text-[12px] font-medium ${
          capped
            ? "text-[#C65C4E] bg-[#FFF3F1] border-[#F0CFC9]"
            : "text-ink-secondary bg-surface border-line"
        }`}
      >
        {freeUsed} of {freeLimit} free analyses used
      </span>
      {capped ? (
        <button
          type="button"
          disabled
          title={`Free limit reached - Reach investors (${pricing.submissionAmountDisplay}) has no limit`}
          className="inline-flex w-full min-h-[36px] items-center justify-center rounded-full bg-accent px-4 text-[13px] font-bold text-accent-ink opacity-50 cursor-not-allowed"
        >
          Analyze a deck
        </button>
      ) : (
        <Link
          to="/upload"
          className="inline-flex w-full min-h-[36px] items-center justify-center rounded-full bg-accent px-4 text-[13px] font-bold text-accent-ink transition hover:bg-accent-press"
        >
          Analyze a deck
        </Link>
      )}
    </div>
  );
}

function FounderDashboard({ data }: { data: FounderData }) {
  const pricing = usePricing();
  const { analyses, freeUsed, freeLimit, applications } = data;
  const capped = freeUsed >= freeLimit;
  const appliedDeckIds = new Set(
    applications.map((a) => a.deck_id).filter((v): v is string => Boolean(v)),
  );

  return (
    <>
      <div className="mb-8">
        <p className="font-script text-accent text-[22px] leading-none">welcome back</p>
        <h1 className="font-display text-[28px] font-semibold mt-1">Your dashboard</h1>
      </div>

      <section className="mb-6 rounded-2xl border border-accent/25 bg-gradient-to-br from-accent/10 to-accent/0 p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="min-w-0">
          <p className="font-display text-[16px] font-semibold text-ink">
            Turn your raise into an always-on engine
          </p>
          <p className="text-ink-secondary text-[13.5px] mt-1 max-w-[560px]">
            ikomatch sends personalized investor emails for you, over WhatsApp - every message hand-written for that specific investor.
          </p>
        </div>
        <IkomatchCTA placement="dashboard_card" variant="button" className="shrink-0">
          Start on WhatsApp →
        </IkomatchCTA>
      </section>




      <section className="bg-surface border border-line rounded-2xl p-6 md:p-8 mb-8">
        <h2 className="font-display text-[20px] font-semibold mb-4">Your analyses</h2>
        {analyses.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-ink-secondary text-[14px] mb-4">
              No analyses yet - analyze your first deck.
            </p>
            {!capped && (
              <Link
                to="/upload"
                className="inline-flex min-h-[40px] items-center justify-center rounded-full bg-accent px-5 text-[13.5px] font-bold text-accent-ink"
              >
                Analyze a deck
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="text-left text-ink-tertiary text-[12px] uppercase tracking-wide border-b border-line">
                  <th className="py-2 pr-4 font-medium">Company</th>
                  <th className="py-2 pr-4 font-medium">Score</th>
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {analyses.map((a) => (
                  <tr key={a.deck_id} className="border-b border-line/60 last:border-0">
                    <td className="py-3 pr-4 text-ink">{a.company}</td>
                    <td className="py-3 pr-4 text-ink font-medium">
                      {a.overall_score ?? "-"}
                      <span className="text-ink-tertiary text-[12px]">/100</span>
                    </td>
                    <td className="py-3 pr-4 text-ink-secondary">
                      {new Date(a.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <div className="flex items-center gap-4">
                        <Link
                          to="/results"
                          search={{ deck: a.deck_id } as never}
                          className="text-accent hover:underline text-[13.5px]"
                        >
                          View →
                        </Link>
                        {appliedDeckIds.has(a.deck_id) ? (
                          <span className="text-ink-tertiary text-[13.5px]">Applied ✓</span>
                        ) : (
                          <Link
                            to="/apply"
                            search={{ deck: a.deck_id } as never}
                            className="text-accent hover:underline text-[13.5px]"
                          >
                            Apply to programs →
                          </Link>
                        )}
                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-surface border border-line rounded-2xl p-6 md:p-8">
        <h2 className="font-display text-[20px] font-semibold mb-4">Your applications</h2>
        {applications.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-ink-secondary text-[14px] mb-4">No applications yet.</p>
            <Link
              to="/apply"
              className="inline-flex min-h-[40px] items-center justify-center rounded-full bg-accent px-5 text-[13.5px] font-bold text-accent-ink"
            >
              Apply to 10+ programs - {pricing.submissionAmountDisplay}
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {applications.map((app) => (
              <ApplicationRow key={app.id} app={app} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

const STATUS_STYLES: Record<string, string> = {
  pending: "bg-surface-sunken text-ink-secondary border-line",
  submitted: "bg-[#E6F5EC] text-[#1F7A3C] border-[#BFE3CD]",
  not_a_fit: "bg-[#FFF3F1] text-[#B04A3D] border-[#F0CFC9]",
};

const FULFILLMENT_STYLES: Record<string, string> = {
  unassigned: "bg-surface-sunken text-ink-secondary",
  assigned: "bg-[#E9F0FA] text-[#2F5FA6]",
  in_progress: "bg-[#FFF6E0] text-[#8A6410]",
  completed: "bg-[#E6F5EC] text-[#1F7A3C]",
};

function ApplicationRow({ app }: { app: FounderApplication }) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState("one-pager.pdf");
  const runGetContent = useServerFn(getOnePagerContent);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  async function preparePdf() {
    if (pdfUrl || downloading) return;
    setDownloading(true);
    try {
      const cr = await runGetContent({ data: { submissionId: app.id } });
      if (cr.ok) {
        const bytes = await buildOnePagerPdf(cr.content);
        const blob = new Blob([new Uint8Array(bytes)], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const base = (cr.content.headline || app.company || "one-pager").slice(0, 40);
        const slug =
          base.replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "one-pager";
        setPdfName(`${slug}-one-pager.pdf`);
        setPdfUrl(url);
      }
    } catch (err) {
      console.error("one-pager pdf failed", err);
      // leave in idle state; user can retry

    } finally {
      setDownloading(false);
    }
  }

  const fulfillmentLabel = app.fulfillment_status.replace(/_/g, " ");

  return (
    <div className="border border-line rounded-xl bg-bg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-surface-sunken/40"
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? (
            <ChevronDown className="h-4 w-4 text-ink-tertiary shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-ink-tertiary shrink-0" />
          )}
          <span className="font-medium text-ink truncate">{app.company}</span>
          <span className="text-ink-tertiary text-[13px] shrink-0">
            · {new Date(app.created_at).toLocaleDateString()}
          </span>
        </div>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12px] font-medium capitalize ${
            FULFILLMENT_STYLES[app.fulfillment_status] ?? FULFILLMENT_STYLES.unassigned
          }`}
        >
          {fulfillmentLabel}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-2 border-t border-line">
          <div className="flex flex-col gap-2 mb-4">
            {app.targets.length === 0 ? (
              <p className="text-ink-tertiary text-[13px]">No matched programs yet.</p>
            ) : (
              app.targets.map((t, i) => {
                const chipStyle = STATUS_STYLES[t.status] ?? STATUS_STYLES.pending;
                const label =
                  t.status === "not_a_fit"
                    ? "Not a fit"
                    : t.status.charAt(0).toUpperCase() + t.status.slice(1);
                return (
                  <div
                    key={i}
                    className="flex items-start justify-between gap-3 border border-line rounded-lg px-3 py-2 bg-surface"
                  >
                    <div className="min-w-0">
                      <div className="text-[14px] text-ink font-medium">{t.program_name}</div>
                      {t.match_reason && (
                        <div className="text-[12.5px] text-ink-secondary mt-0.5">
                          {t.match_reason}
                        </div>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11.5px] font-medium shrink-0 ${chipStyle}`}
                    >
                      {label}
                    </span>
                  </div>
                );
              })
            )}
          </div>
          {app.has_one_pager ? (
            pdfUrl ? (
              <a
                href={pdfUrl}
                download={pdfName}
                className="inline-flex items-center gap-2 rounded-full border border-line bg-accent px-4 py-2 text-[13px] font-semibold text-accent-ink hover:opacity-90"
              >
                <Download className="h-3.5 w-3.5" />
                Download one-pager (PDF)
              </a>
            ) : (
              <button
                type="button"
                onClick={preparePdf}
                disabled={downloading}
                className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-4 py-2 text-[13px] font-semibold text-ink hover:bg-surface-sunken disabled:opacity-60"
              >
                <Download className="h-3.5 w-3.5" />
                {downloading ? "Preparing…" : "Download one-pager (PDF)"}
              </button>
            )
          ) : (
            <span className="text-[13px] text-ink-tertiary">One-pager preparing…</span>
          )}
        </div>
      )}
    </div>
  );
}
